import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

function SensorGasUdara() {
  // --- SEO Meta Tags untuk Halaman Sensor Gas & Udara ---
  useEffect(() => {
    document.title = "Sensor Gas & Kualitas Udara - Solusi IoT Cerdas | Lingkungan Aman & Sehat";

    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', "Pantau kualitas udara dan deteksi kebocoran gas berbahaya secara real-time dengan sensor Solusi IoT Cerdas. Tingkatkan keamanan dan kesehatan lingkungan Anda.");

    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute('content', "sensor gas, kualitas udara, AQI, CO level, LPG detector, IoT keamanan, pemantauan lingkungan");

    let linkCanonical = document.querySelector('link[rel="canonical"]');
    if (!linkCanonical) {
      linkCanonical = document.createElement('link');
      linkCanonical.setAttribute('rel', 'canonical');
      document.head.appendChild(linkCanonical);
    }
    linkCanonical.setAttribute('href', "https://www.Solusi IoT Cerdas.com/produk/sensor/gas-kualitas-udara"); // Ganti dengan URL halaman sebenarnya

    // Cleanup function: Akan dijalankan saat komponen di-unmount atau sebelum efek berikutnya
    return () => {
      document.title = "Solusi IoT Cerdas Solusi Cerdas IoT"; // Kembalikan ke judul default aplikasi
    };
  }, []); // Dependensi kosong agar useEffect hanya dijalankan sekali (saat mount)

  // Inisialisasi state dengan nilai awal yang valid
  const [coLevel, setCoLevel] = useState(25); // ppm
  const [lpgLevel, setLpgLevel] = useState(50); // ppm
  const [airQualityIndex, setAirQualityIndex] = useState(65); // AQI scale 0-500

  // State untuk riwayat data harian (untuk grafik)
  const [coHistory, setCoHistory] = useState(() => {
    const history = [];
    const today = new Date();
    for (let i = 6; i >= 0; i--) { // Data untuk 7 hari terakhir
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      const dayName = date.toLocaleDateString('id-ID', { weekday: 'short' });
      const value = parseFloat((20 + Math.random() * 10 - 5).toFixed(0)); // Fluktuasi kecil
      history.push({ day: dayName, date: date.toISOString().slice(0, 10), value: value });
    }
    return history;
  });

  const [aqiHistory, setAqiHistory] = useState(() => {
    const history = [];
    const today = new Date();
    for (let i = 6; i >= 0; i--) { // Data untuk 7 hari terakhir
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      const dayName = date.toLocaleDateString('id-ID', { weekday: 'short' });
      const value = parseFloat((55 + Math.random() * 20 - 10).toFixed(0)); // Fluktuasi kecil
      history.push({ day: dayName, date: date.toISOString().slice(0, 10), value: value });
    }
    return history;
  });

  // Fungsi untuk mendapatkan warna berdasarkan AQI (untuk teks dan border)
  const getAqiColorClass = (aqi) => {
    if (aqi <= 50) return 'text-green-500 dark:text-green-400'; // Baik
    if (aqi <= 100) return 'text-yellow-500 dark:text-yellow-400'; // Sedang
    if (aqi <= 150) return 'text-orange-500 dark:text-orange-400'; // Tidak Sehat bagi Kelompok Sensitif
    if (aqi <= 200) return 'text-red-500 dark:text-red-400'; // Tidak Sehat
    if (aqi <= 300) return 'text-purple-700 dark:text-purple-500'; // Sangat Tidak Sehat
    return 'text-gray-900 dark:text-gray-100'; // Berbahaya (atau warna default)
  };

  const getAqiStatus = (aqi) => {
    if (aqi <= 50) return 'Baik';
    if (aqi <= 100) return 'Sedang';
    if (aqi <= 150) return 'Tidak Sehat (Sensitif)';
    if (aqi <= 200) return 'Tidak Sehat';
    if (aqi <= 300) return 'Sangat Tidak Sehat';
    return 'Berbahaya';
  };

  const getAqiChartColor = (aqi) => {
    if (aqi <= 50) return { bg: 'rgba(16, 185, 129, 0.2)', border: '#10b981' }; // green-500
    if (aqi <= 100) return { bg: 'rgba(234, 179, 8, 0.2)', border: '#eab308' }; // yellow-500
    if (aqi <= 150) return { bg: 'rgba(249, 115, 22, 0.2)', border: '#f97316' }; // orange-500
    if (aqi <= 200) return { bg: 'rgba(239, 68, 68, 0.2)', border: '#ef4444' }; // red-500
    return { bg: 'rgba(124, 58, 237, 0.2)', border: '#7c3aed' }; // purple-600
  };


  useEffect(() => {
    const interval = setInterval(() => {
      const newTime = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });

      // Update current levels
      setCoLevel(prev => parseFloat(Math.max(0, Math.min(100, prev + (Math.random() * 10 - 5))).toFixed(0)));
      setLpgLevel(prev => parseFloat(Math.max(0, Math.min(200, prev + (Math.random() * 15 - 7.5))).toFixed(0)));
      setAirQualityIndex(prev => parseFloat(Math.max(0, Math.min(200, prev + (Math.random() * 10 - 5))).toFixed(0)));

      // Update daily history for charts (adding small increments to "today's" value)
      // Gunakan fungsi updater untuk mengakses nilai terbaru, meskipun dependensi []
      setCoHistory(prevHistory => {
        const updatedHistory = [...prevHistory];
        const todayIndex = updatedHistory.length - 1;
        // Pastikan indeks ada sebelum diakses
        if (updatedHistory[todayIndex]) {
          updatedHistory[todayIndex] = {
            ...updatedHistory[todayIndex],
            value: parseFloat((updatedHistory[todayIndex].value + (Math.random() * 2 - 1)).toFixed(0)) // small random change
          };
        }
        return updatedHistory;
      });

      setAqiHistory(prevHistory => {
        const updatedHistory = [...prevHistory];
        const todayIndex = updatedHistory.length - 1;
        // Pastikan indeks ada sebelum diakses
        if (updatedHistory[todayIndex]) {
          updatedHistory[todayIndex] = {
            ...updatedHistory[todayIndex],
            value: parseFloat((updatedHistory[todayIndex].value + (Math.random() * 3 - 1.5)).toFixed(0)) // small random change
          };
        }
        return updatedHistory;
      });

    }, 5000); // Update setiap 5 detik

    return () => clearInterval(interval); // Cleanup interval on unmount
  }, []); // Dependensi kosong agar hanya menjalankan sekali (initial setup)

  // Data dan opsi untuk grafik CO Level
  const coChartData = {
    labels: coHistory.map(data => data.day),
    datasets: [
      {
        label: 'CO Level Harian (ppm)',
        data: coHistory.map(data => data.value),
        fill: true,
        backgroundColor: 'rgba(99, 102, 241, 0.2)', // indigo-500 with opacity
        borderColor: '#6366f1', // indigo-500
        tension: 0.3,
        pointBackgroundColor: '#6366f1',
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: '#6366f1',
      },
    ],
  };

  const coChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: 'var(--text-color)',
        },
      },
      title: {
        display: true,
        text: 'Riwayat Level Karbon Monoksida (CO) 7 Hari Terakhir',
        color: 'var(--text-color)',
        font: {
          size: 18,
        },
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += new Intl.NumberFormat('id-ID', { maximumFractionDigits: 0 }).format(context.parsed.y) + ' ppm';
            }
            return label;
          }
        }
      }
    },
    scales: {
      x: {
        ticks: { color: 'var(--text-color)' },
        grid: { color: 'rgba(100, 100, 100, 0.1)' },
      },
      y: {
        ticks: {
          color: 'var(--text-color)',
          callback: function(value) { return value + ' ppm'; }
        },
        grid: { color: 'rgba(100, 100, 100, 0.1)' },
        min: 0,
      },
    },
  };

  // Data dan opsi untuk grafik AQI
  const aqiChartData = {
    labels: aqiHistory.map(data => data.day),
    datasets: [
      {
        label: 'Indeks Kualitas Udara Harian (AQI)',
        data: aqiHistory.map(data => data.value),
        fill: true,
        backgroundColor: (context) => {
          const chart = context.chart;
          const { ctx, chartArea } = chart;
          if (!chartArea) return; // Ensure chartArea is defined

          // Pastikan ada data sebelum mencoba membuat gradient
          if (aqiHistory.length === 0) {
            return 'rgba(0,0,0,0)'; // Kembali ke warna transparan jika tidak ada data
          }

          const gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
          // Dynamically set colors based on AQI levels (simplified for gradient)
          // Tambahkan fallback `|| 0` untuk menghindari undefined
          gradient.addColorStop(0, getAqiChartColor(aqiHistory[0]?.value || 0).bg);
          gradient.addColorStop(0.5, getAqiChartColor(aqiHistory[Math.floor(aqiHistory.length / 2)]?.value || 0).bg);
          gradient.addColorStop(1, getAqiChartColor(aqiHistory[aqiHistory.length - 1]?.value || 0).bg);
          return gradient;
        },
        borderColor: (context) => {
          // Pastikan context.parsed ada dan memiliki properti y
          const aqiValue = context.parsed && context.parsed.y !== undefined ? context.parsed.y : 0;
          return getAqiChartColor(aqiValue).border;
        },
        tension: 0.3,
        pointBackgroundColor: (context) => {
          // Pastikan context.parsed ada dan memiliki properti y
          const aqiValue = context.parsed && context.parsed.y !== undefined ? context.parsed.y : 0;
          return getAqiChartColor(aqiValue).border;
        },
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: (context) => {
          // Pastikan context.parsed ada dan memiliki properti y
          const aqiValue = context.parsed && context.parsed.y !== undefined ? context.parsed.y : 0;
          return getAqiChartColor(aqiValue).border;
        },
      },
    ],
  };

  const aqiChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: 'var(--text-color)',
        },
      },
      title: {
        display: true,
        text: 'Riwayat Indeks Kualitas Udara (AQI) 7 Hari Terakhir',
        color: 'var(--text-color)',
        font: {
          size: 18,
        },
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null && context.parsed.y !== undefined) { // Tambahkan cek undefined
              label += new Intl.NumberFormat('id-ID', { maximumFractionDigits: 0 }).format(context.parsed.y) + ' AQI (' + getAqiStatus(context.parsed.y) + ')';
            }
            return label;
          }
        }
      }
    },
    scales: {
      x: {
        ticks: { color: 'var(--text-color)' },
        grid: { color: 'rgba(100, 100, 100, 0.1)' },
      },
      y: {
        ticks: {
          color: 'var(--text-color)',
          callback: function(value) { return value + ' AQI'; }
        },
        grid: { color: 'rgba(100, 100, 100, 0.1)' },
        min: 0,
        max: 300, // AQI biasanya sampai 500, tapi untuk simulasi mungkin cukup 300
      },
    },
  };

  return (
    <div className="container mx-auto p-4 md:p-8 bg-gray-50 text-gray-800 font-sans
                    dark:bg-gray-900 dark:text-gray-200 transition-colors duration-500">
      {/* Hero Section */}
      <section
        className="relative bg-gradient-to-br from-indigo-700 to-purple-800 text-white rounded-lg shadow-xl p-6 md:p-12 mb-8 text-center overflow-hidden
                   dark:from-gray-800 dark:to-gray-900 transition-colors duration-500"
        style={{ backgroundImage: 'url(./assets/images/hero-air-quality.png)', backgroundSize: 'cover', backgroundPosition: 'center' }}
      >
        <div className="absolute inset-0 bg-black opacity-40 rounded-lg"></div>
        <div className="relative z-10">
          <h1 className="text-3xl md:text-5xl font-extrabold mb-4 animate-fade-in-down">
            Jaga Kualitas Udara & Keamanan Lingkungan: Sensor Gas & Udara Solusi IoT Cerdas
          </h1>
          <p className="text-lg md:text-xl max-w-3xl mx-auto mb-8 animate-fade-in">
            Pantau kualitas udara di sekitar Anda, deteksi potensi kebocoran gas berbahaya, dan ambil tindakan cepat.
          </p>
          <div className="flex flex-col md:flex-row justify-center gap-6 mt-8">
            <div className="bg-white text-indigo-800 rounded-lg p-6 shadow-md flex-1 md:max-w-xs transition-all duration-300 hover:scale-105
                            dark:bg-gray-800 dark:text-indigo-200">
              <h3 className="text-xl font-semibold mb-2">Karbon Monoksida (CO)</h3>
              <h2 className="text-4xl font-bold">{coLevel} ppm</h2>
              <p className="text-sm text-gray-600 mt-2 dark:text-gray-400">Level Saat Ini</p>
            </div>
            <div className="bg-white text-indigo-800 rounded-lg p-6 shadow-md flex-1 md:max-w-xs transition-all duration-300 hover:scale-105
                            dark:bg-gray-800 dark:text-indigo-200">
              <h3 className="text-xl font-semibold mb-2">LPG/Metana</h3>
              <h2 className="text-4xl font-bold">{lpgLevel} ppm</h2>
              <p className="text-sm text-gray-600 mt-2 dark:text-gray-400">Level Saat Ini</p>
            </div>
            <div className="bg-white text-indigo-800 rounded-lg p-6 shadow-md flex-1 md:max-w-xs transition-all duration-300 hover:scale-105
                            dark:bg-gray-800 dark:text-indigo-200">
              <h3 className="text-xl font-semibold mb-2">Indeks Kualitas Udara</h3>
              <h2 className={`text-4xl font-bold ${getAqiColorClass(airQualityIndex)}`}>{airQualityIndex} AQI</h2>
              <p className="text-sm text-gray-600 mt-2 dark:text-gray-400">Status: {getAqiStatus(airQualityIndex)}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Bagian Grafik CO Level */}
      <section className="bg-white rounded-lg shadow-md p-6 md:p-8 mb-8
                          dark:bg-gray-800 dark:shadow-xl transition-colors duration-500">
        <h2 className="text-3xl font-bold text-gray-900 text-center mb-6 dark:text-gray-100">
          Tren Level Karbon Monoksida (CO) Harian
        </h2>
        <div className="h-80 w-full"> {/* Atur tinggi grafik */}
          <Line data={coChartData} options={coChartOptions} key="co-chart" />
        </div>
        <p className="text-center text-gray-600 text-sm mt-4 dark:text-gray-400">
          Data ini disimulasikan dan diperbarui secara *real-time*.
        </p>
      </section>

      {/* Bagian Grafik AQI */}
      <section className="bg-white rounded-lg shadow-md p-6 md:p-8 mb-8
                          dark:bg-gray-800 dark:shadow-xl transition-colors duration-500">
        <h2 className="text-3xl font-bold text-gray-900 text-center mb-6 dark:text-gray-100">
          Tren Indeks Kualitas Udara (AQI) Harian
        </h2>
        <div className="h-80 w-full"> {/* Atur tinggi grafik */}
          <Line data={aqiChartData} options={aqiChartOptions} key="aqi-chart" />
        </div>
        <p className="text-center text-gray-600 text-sm mt-4 dark:text-gray-400">
          Data ini disimulasikan dan diperbarui secara *real-time*. Warna grafik akan berubah sesuai status AQI.
        </p>
      </section>


      {/* Why Monitor Section */}
      <section className="bg-white rounded-lg shadow-md p-6 md:p-8 mb-8
                          dark:bg-gray-800 dark:shadow-xl transition-colors duration-500">
        <h2 className="text-3xl font-bold text-indigo-700 text-center mb-6 dark:text-indigo-400">Lingkungan Aman, Hidup Sehat</h2>
        <p className="text-lg text-gray-700 text-center mb-6 max-w-3xl mx-auto dark:text-gray-300">
          Kualitas udara yang buruk dan keberadaan gas berbahaya dapat berdampak serius pada kesehatan manusia dan keamanan aset.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-6">
          <div className="bg-indigo-50 p-6 rounded-lg text-center shadow-sm transition-all duration-300 transform hover:-translate-y-1 hover:shadow-md
                          dark:bg-gray-700 dark:shadow-lg">
            <img src="./assets/icons/icon-health.png" alt="Health Icon" className="mx-auto mb-4 w-16 h-16 animate-bounce-subtle" />
            <h3 className="text-xl font-semibold text-indigo-600 mb-2 dark:text-indigo-300">Kesehatan Optimal</h3>
            <p className="text-gray-700 dark:text-gray-300">Hindari masalah pernapasan dan alergi akibat polusi udara.</p>
          </div>
          <div className="bg-indigo-50 p-6 rounded-lg text-center shadow-sm transition-all duration-300 transform hover:-translate-y-1 hover:shadow-md
                          dark:bg-gray-700 dark:shadow-lg">
            <img src="./assets/icons/icon-security.png" alt="Security Icon" className="mx-auto mb-4 w-16 h-16 animate-bounce-subtle" />
            <h3 className="text-xl font-semibold text-indigo-600 mb-2 dark:text-indigo-300">Keamanan Terjamin</h3>
            <p className="text-gray-700 dark:text-gray-300">Deteksi dini kebocoran gas untuk mencegah kebakaran atau keracunan.</p>
          </div>
          <div className="bg-indigo-50 p-6 rounded-lg text-center shadow-sm transition-all duration-300 transform hover:-translate-y-1 hover:shadow-md
                          dark:bg-gray-700 dark:shadow-lg">
            <img src="./assets/icons/icon-comfort.png" alt="Comfort Icon" className="mx-auto mb-4 w-16 h-16 animate-bounce-subtle" />
            <h3 className="text-xl font-semibold text-indigo-600 mb-2 dark:text-indigo-300">Kenyamanan Interior</h3>
            <p className="text-gray-700 dark:text-gray-300">Pastikan sirkulasi udara yang baik dan bebas bau di dalam ruangan.</p>
          </div>
          <div className="bg-indigo-50 p-6 rounded-lg text-center shadow-sm transition-all duration-300 transform hover:-translate-y-1 hover:shadow-md
                          dark:bg-gray-700 dark:shadow-lg">
            <img src="./assets/icons/icon-efficiency.png" alt="Efficiency Icon" className="mx-auto mb-4 w-16 h-16 animate-bounce-subtle" />
            <h3 className="text-xl font-semibold text-indigo-600 mb-2 dark:text-indigo-300">Efisiensi Lingkungan</h3>
            <p className="text-gray-700 dark:text-gray-300">Pahami sumber polusi dan cari solusi yang berkelanjutan untuk lingkungan.</p>
          </div>
        </div>
      </section>

      {/* Sensor Gas Section */}
      <section className="bg-white rounded-lg shadow-md p-6 md:p-8 mb-8
                          dark:bg-gray-800 dark:shadow-xl transition-colors duration-500">
        <h2 className="text-3xl font-bold text-indigo-700 text-center mb-6 dark:text-indigo-400">Deteksi Dini Bahaya Tak Terlihat: Sensor Gas</h2>
        <p className="text-lg text-gray-700 text-center mb-6 max-w-3xl mx-auto dark:text-gray-300">
          Solusi IoT Cerdas terintegrasi dengan berbagai sensor gas populer dari seri MQ.
        </p>
        <div className="flex flex-col md:flex-row items-center gap-8">
          <div className="w-full md:w-1/2">
            <img src="./assets/images/gas-sensor-bg.png" alt="Gas Sensor Illustration" className="rounded-lg shadow-lg w-full h-auto" />
          </div>
          <ul className="list-none p-0 w-full md:w-1/2 space-y-4">
            <li className="bg-indigo-50 p-4 rounded-md border-l-4 border-indigo-500 shadow-sm flex items-center gap-3
                            dark:bg-gray-700 dark:border-indigo-700 dark:text-gray-200 dark:shadow-lg transition-colors duration-500">
              <img src="./assets/icons/icon-alert.png" alt="Alert Icon" className="w-8 h-8 flex-shrink-0" />
              <div>
                <strong className="text-indigo-800 dark:text-indigo-300">Identifikasi Gas Spesifik</strong>: Mampu mendeteksi berbagai jenis gas berbahaya.
              </div>
            </li>
            <li className="bg-indigo-50 p-4 rounded-md border-l-4 border-indigo-500 shadow-sm flex items-center gap-3
                            dark:bg-gray-700 dark:border-indigo-700 dark:text-gray-200 dark:shadow-lg transition-colors duration-500">
              <img src="./assets/icons/icon-chart.png" alt="Chart Icon" className="w-8 h-8 flex-shrink-0" />
              <div>
                <strong className="text-indigo-800 dark:text-indigo-300">Pembacaan Real-time</strong>: Dapatkan level konsentrasi gas secara *real-time*.
              </div>
            </li>
            <li className="bg-indigo-50 p-4 rounded-md border-l-4 border-indigo-500 shadow-sm flex items-center gap-3
                            dark:bg-gray-700 dark:border-indigo-700 dark:text-gray-200 dark:shadow-lg transition-colors duration-500">
              <img src="./assets/icons/icon-alert.png" alt="Threshold Icon" className="w-8 h-8 flex-shrink-0" />
              <div>
                <strong className="text-indigo-800 dark:text-indigo-300">Ambang Batas & Alerte</strong>: Atur level aman dan terima notifikasi instan.
              </div>
            </li>
          </ul>
        </div>
      </section>

      {/* Air Quality Sensor Section */}
      <section className="bg-white rounded-lg shadow-md p-6 md:p-8 mb-8
                          dark:bg-gray-800 dark:shadow-xl transition-colors duration-500">
        <h2 className="text-3xl font-bold text-indigo-700 text-center mb-6 dark:text-indigo-400">Pahami Kualitas Udara di Sekitar Anda: Partikulat & VOCs</h2>
        <p className="text-lg text-gray-700 text-center mb-6 max-w-3xl mx-auto dark:text-gray-300">
          Selain deteksi gas spesifik, Solusi IoT Cerdas juga mendukung sensor untuk memantau kualitas udara secara umum.
        </p>
        <div className="flex flex-col-reverse md:flex-row items-center gap-8">
          <ul className="list-none p-0 w-full md:w-1/2 space-y-4">
            <li className="bg-indigo-50 p-4 rounded-md border-l-4 border-indigo-500 shadow-sm flex items-center gap-3
                            dark:bg-gray-700 dark:border-indigo-700 dark:text-gray-200 dark:shadow-lg transition-colors duration-500">
              <img src="./assets/icons/icon-chart.png" alt="AQI Icon" className="w-8 h-8 flex-shrink-0" />
              <div>
                <strong className="text-indigo-800 dark:text-indigo-300">Indeks Kualitas Udara (AQI)</strong>: Konversi pembacaan sensor menjadi indeks yang mudah dipahami.
              </div>
            </li>
            <li className="bg-indigo-50 p-4 rounded-md border-l-4 border-indigo-500 shadow-sm flex items-center gap-3
                            dark:bg-gray-700 dark:border-indigo-700 dark:text-gray-200 dark:shadow-lg transition-colors duration-500">
              <img src="./assets/icons/icon-alert.png" alt="Pollutant Icon" className="w-8 h-8 flex-shrink-0" />
              <div>
                <strong className="text-indigo-800 dark:text-indigo-300">Deteksi Polutan Umum</strong>: Identifikasi keberadaan debu, asap, uap kimia, atau polutan lain.
              </div>
            </li>
            <li className="bg-indigo-50 p-4 rounded-md border-l-4 border-indigo-500 shadow-sm flex items-center gap-3
                            dark:bg-gray-700 dark:border-indigo-700 dark:text-gray-200 dark:shadow-lg transition-colors duration-500">
              <img src="./assets/icons/icon-chart.png" alt="History Icon" className="w-8 h-8 flex-shrink-0" />
              <div>
                <strong className="text-indigo-800 dark:text-indigo-300">Visualisasi Sejarah</strong>: Lacak perubahan kualitas udara harian atau mingguan.
              </div>
            </li>
          </ul>
          <div className="w-full md:w-1/2">
            <img src="./assets/images/air-quality-sensor-bg.png" alt="Air Quality Sensor Illustration" className="rounded-lg shadow-lg w-full h-auto" />
          </div>
        </div>
      </section>

      {/* Combined Scenarios Section */}
      <section className="bg-white rounded-lg shadow-md p-6 md:p-8 mb-8
                          dark:bg-gray-800 dark:shadow-xl transition-colors duration-500">
        <h2 className="text-3xl font-bold text-indigo-700 text-center mb-6 dark:text-indigo-400">Solusi Cerdas untuk Lingkungan Anda</h2>
        <p className="text-lg text-gray-700 text-center mb-6 max-w-3xl mx-auto dark:text-gray-300">
          Kombinasi sensor gas dan udara melalui Solusi IoT Cerdas memungkinkan otomasi cerdas dan respons proaktif:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-purple-50 p-6 rounded-lg shadow-sm transition-all duration-300 transform hover:-translate-y-1 hover:shadow-md
                          dark:bg-gray-700 dark:shadow-lg">
            <h3 className="text-xl font-semibold text-purple-700 mb-2 dark:text-purple-400">Sistem Peringatan Dini Kebocoran Gas</h3>
            <p className="text-gray-700 dark:text-gray-300">Otomatis menyalakan kipas ventilasi atau mengirim *alert* ke ponsel Anda.</p>
          </div>
          <div className="bg-purple-50 p-6 rounded-lg shadow-sm transition-all duration-300 transform hover:-translate-y-1 hover:shadow-md
                          dark:bg-gray-700 dark:shadow-lg">
            <h3 className="text-xl font-semibold text-purple-700 mb-2 dark:text-purple-400">Pengontrol Kualitas Udara Otomatis</h3>
            <p className="text-gray-700 dark:text-gray-300">Jika level polutan tinggi, otomatis aktifkan *air purifier* atau beri tahu Anda untuk membuka jendela.</p>
          </div>
          <div className="bg-purple-50 p-6 rounded-lg shadow-sm transition-all duration-300 transform hover:-translate-y-1 col-span-1 md:col-span-2
                          dark:bg-gray-700 dark:shadow-lg">
            <h3 className="text-xl font-semibold text-purple-700 mb-2 dark:text-purple-400">Pemantauan Lingkungan Kerja</h3>
            <p className="text-gray-700 dark:text-gray-300">Pastikan area kerja tetap aman dari paparan gas berbahaya.</p>
          </div>
        </div>
      </section>

      {/* Getting Started Section */}
      <section className="bg-white rounded-lg shadow-md p-6 md:p-8 text-center mb-8
                          dark:bg-gray-800 dark:shadow-xl transition-colors duration-500">
        <h2 className="text-3xl font-bold text-indigo-700 mb-6 dark:text-indigo-400">Siap Ciptakan Lingkungan yang Lebih Aman & Sehat?</h2>
        <p className="text-lg text-gray-700 mb-6 max-w-2xl mx-auto dark:text-gray-300">
          Integrasi sensor gas dan udara ke Solusi IoT Cerdas adalah langkah mudah untuk keamanan dan kesehatan yang lebih baik.
        </p>
        <button className="bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-8 rounded-lg text-xl
                           transition-all duration-300 ease-in-out transform hover:scale-105"
                onClick={() => alert('Link ke Panduan Integrasi atau halaman Pendaftaran!')}>
          Pelajari Panduan Integrasi & Daftar Sekarang
        </button>
      </section>
    </div>
  );
}

export default SensorGasUdara;